// --------------------------------------------------------------------------
// BLE service for the Spinnax Freak
// --------------------------------------------------------------------------

// --------------------------------------------------------------------------
// Imports
// --------------------------------------------------------------------------
import {
  action,
  IObservableValue,
  IValueDidChange,
  observable,
  runInAction,
  toJS,
} from 'mobx';
import { PartialObserver, Subject } from 'rxjs';

import _ from 'lodash';
import { Device } from 'util/device';
import { BLEOriginal_ } from '..';
import { BluetoothInfo } from '../../../models/BluetoothInfo';
import {
  BLE_CHARACTERISTIC_CMD,
  BLE_CHARACTERISTIC_RSP,
  BLE_MTU,
  BLE_SERVICE_SPINNAX,
  ID_DEVICE_DEFAULT,
} from '../../../spinnaxBase/constants';
import * as Pck from '../../../spinnaxBase/logic/Com_BLE_packets';
import {
  packetIdToName,
  PckCmdInfo,
  PckRspIdBeg,
  PckRspInfo,
  PckRspLog,
  PckRspReTrg,
} from '../../../spinnaxBase/logic/Com_BLE_packets';
import { numberArrayToString } from '../../../spinnaxBase/logic/utilities';
import {
  BLCmd,
  BLCmds,
  BLEDongleState,
  BLRsp,
  BLRsps,
  BluetoothEntry,
  COM,
} from '../../../types';
import { extractBLEDongleState, extractMac } from '../../../util';
import { logger } from '../../../services/Logger';

// --------------------------------------------------------------------------
// Constants
// --------------------------------------------------------------------------

const merger = (objValue: any, srcValue: any) => {
  if (_.isArray(objValue)) return objValue.concat(srcValue);
  if (_.isObjectLike(objValue)) return merge(objValue, srcValue);
  return srcValue ?? objValue;
};
const merge: typeof _.defaults = _.partialRight(_.mergeWith, merger);

const extractOSVersion = (info: PckRspInfo): string => {
  const state = info.dongle_state;
  return `${state.version_os_major}.${state.version_os_minor}.${state.version_os_patch}.${state.version_os_build}`;
};

const cmdName = (buf: ArrayBuffer) => {
  const dataView = new DataView(buf);
  const id = dataView.getUint8(0);
  return packetIdToName(id);
};

type Resolve<T> = (value: T | PromiseLike<T>) => void;
type Reject = (reason?: any) => void;

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export enum StatesBLE {
  CONNECTED = 'CONNECTED',
  CONNECTING = 'CONNECTING',
  DISCONNECTED = 'DISCONNECTED',
  ERROR = 'ERROR',
}

class BluetoothLEService {
  // --------------------------------------------------------------------------
  // Private attributes
  // --------------------------------------------------------------------------
  private _ble: BLEOriginal_;
  // private _device: BluetoothInfo;
  private _isScanning = false;
  private _failedConnectionsSinceConnect: Record<string, number> = {};
  private _autoConnectingTo: Record<string, Subject<BluetoothEntry>> = {};
  private _waitingCommands: Record<
    string,
    [Resolve<BLRsp>, Reject, ReturnType<typeof setTimeout>]
  > = {};

  // --------------------------------------------------------------------------
  // Observable attributes
  // --------------------------------------------------------------------------

  connectionState: IObservableValue<StatesBLE> = observable.box(
    StatesBLE.DISCONNECTED
  );
  private setConnectionState = (value: StatesBLE) =>
    runInAction(() => this.connectionState.set(value));
  bleDevice: IObservableValue<BluetoothInfo | null> = observable.box(null, {
    name: 'bl.bleDevice',
  });
  deviceState: IObservableValue<BLEDongleState | null> = observable.box(null, {
    name: 'bl.deviceState',
  });

  /** gets all data from the Freak */
  dataObserver: Subject<BLRsp> = new Subject();
  /** gets only RecordSample data from the Freak */
  recordObserver: Subject<COM.RecordSample> = new Subject();

  // --------------------------------------------------------------------------
  // Constructor and destructor
  // --------------------------------------------------------------------------

  constructor(ble: BLEOriginal_) {
    logger.info('🟢 ble - constructor - Setup BLE service');
    this._ble = ble;
  }

  destroy() {
    this.resetWaitingCommands();
    logger.info('🟢 ble - destroy - Destroyed BLE service');
  }

  // --------------------------------------------------------------------------
  // Methods
  // --------------------------------------------------------------------------

  private resetWaitingCommands() {
    logger.warn('resetting waiting commands!');
    _.forOwn(this._waitingCommands, ([_resolve, _reject, timeout]) => {
      clearTimeout(timeout);
    });
    this._waitingCommands = {};
  }

  // private setBleDevice(dev: BluetoothInfo) {
  //   if (!dev.entry?.mac) {
  //     const mac = extractMac(toJS(dev.entry)!);
  //     merge(dev, { entry: { mac } });
  //   }
  //   runInAction(() => this.bleDevice.set(dev as BluetoothInfo));
  // }

  private setBleDevice = action('setBleDevice', (dev: BluetoothInfo) => {
    if (!dev.entry?.mac) {
      const mac = extractMac(toJS(dev.entry)!);
      merge(dev, { entry: { mac } });
    }
    this.bleDevice.set(dev as BluetoothInfo);
  });

  // --------------------------------------------------------------------------
  // Scan handling
  // --------------------------------------------------------------------------

  startScan(
    services: Array<string>,
    duration: number,
    observer: PartialObserver<BluetoothEntry>
  ) {
    this._isScanning = true;
    if (Device.platform === 'ios') {
      // ios doesn't show already connected devices on scan so fake it!
      logger.info(
        '🟡 ble - startScan - iOS detected, checking connected devices'
      );
      this._ble
        .connectedPeripheralsWithServices(services)
        .then((devices: BluetoothEntry[]) => {
          if (this._isScanning)
            devices.forEach((d) => {
              const mac = extractMac(d);
              const the_device = merge(d, { mac });
              if (this._isScanning) observer.next?.(the_device);
            });
        })
        .catch(observer.error);
    }
    const subject = this._ble.startScan(services);
    const subscription = subject.subscribe({
      next: (d) => {
        const mac = extractMac(d);
        const the_device = merge(d, { mac });
        if (this._isScanning) observer.next?.(the_device);
      },
      complete: observer?.complete,
      error: observer?.error,
    });
    setTimeout(
      () =>
        this._ble.stopScan().then(() => {
          // @ts-ignore
          if (!subscription.closed) observer.complete?.();
        }),
      duration
    );
  }

  stopScan() {
    this._isScanning = false;
    this._ble.stopScan();
  }

  // --------------------------------------------------------------------------
  // Connection handling
  // --------------------------------------------------------------------------

  async connect(deviceId: string): Promise<boolean> {
    return new Promise((res, rej) => {
      this._ble.connect(deviceId).subscribe({
        next: async (peripheralData) => {
          try {
            runInAction(() => {
              this.onConnectionUp(peripheralData, true);
              this.connectionState.set(StatesBLE.CONNECTED);
            });
            res(true);
          } catch (error) {
            rej(error);
          }
        },
        error: (error) => {
          runInAction(() => this.connectionState.set(StatesBLE.DISCONNECTED));
          logger.info('🔴 ble - connect - disconnected from');
          rej(error);
        },
        complete: () => {
          logger.info('🟢 ble - connect - connection complete');
        },
      });
    });
  }

  private cleanup(deviceId: string) {
    // Aufräumen aller Verbindungsversuche
    if (this._autoConnectingTo[deviceId]) {
      this._autoConnectingTo[deviceId].complete();
      delete this._autoConnectingTo[deviceId];
    }
    this.updateConnectionState(StatesBLE.DISCONNECTED);
  }

  autoConnect(
    deviceId: string,
    timeoutMs: number = 15000
  ): Subject<BluetoothEntry> {
    // Bestehende Verbindungsversuche aufräumen
    this.cleanup(deviceId);

    const subj = new Subject<BluetoothEntry>();
    this._autoConnectingTo[deviceId] = subj;
    this.updateConnectionState(StatesBLE.CONNECTING);

    const timeoutId = setTimeout(() => {
      logger.error(
        `🟡 ble - autoConnect - Connection attempt to ${deviceId} timed out after ${timeoutMs}ms`
      );
      this.cleanup(deviceId);
    }, timeoutMs);

    this._ble.autoConnect(
      deviceId,
      (peripheral: BluetoothEntry) => {
        clearTimeout(timeoutId);
        subj.next(peripheral);
        runInAction(() => this.onConnectionUp(peripheral, true));
      },
      (peripheral: BluetoothEntry) => {
        clearTimeout(timeoutId);
        runInAction(() => {
          this.cleanup(deviceId);
          subj.error(peripheral);
        });
      }
    );

    return subj;
  }

  notificationObserver = {
    next: (data: any) => runInAction(() => this.onData(data)),
    error: logger.error,
  };

  // --------------------------------------------------------------------------
  // Connection handling
  // --------------------------------------------------------------------------

  updateConnectionState(state: StatesBLE) {
    runInAction(() => {
      this.setConnectionState(state);
    });
  }

  async onConnectionUp(
    device: BluetoothEntry,
    force: boolean = false
  ): Promise<boolean> {
    if (!device) {
      logger.error(
        `🔴 ble - onConnectionUp - device is undefined, cannot start notification`
      );
      this.updateConnectionState(StatesBLE.DISCONNECTED);
      return false;
    }

    try {
      // MTU anfordern (nur Android)
      if (Device.platform === 'android' && device.id) {
        try {
          await this._ble.requestMtu(device.id, BLE_MTU);
          logger.info(
            `🟢 ble - onConnectionUp - MTU request successful for ${device.id}`
          );
        } catch (err) {
          logger.warn('🟡 ble - onConnectionUp - MTU request failed:', err);
          return false;
        }
      }

      // Geräteinfo setzen
      this.setBleDevice({
        entry: device,
        info: null,
        autoConnect: this._autoConnectingTo[device.id] !== undefined,
      });

      // Status auf CONNECTED setzen
      this.updateConnectionState(StatesBLE.CONNECTED);

      if (force || this.connectionState.get() === StatesBLE.CONNECTED) {
        // Sicherstellen, dass device.id existiert
        if (!device.id) {
          logger.error(
            `🔴 ble - onConnectionUp - device.id is undefined, cannot start notification`
          );
          this.updateConnectionState(StatesBLE.DISCONNECTED);
          return false;
        }

        try {
          // Notification starten
          this._ble
            .startNotification(
              device.id,
              BLE_SERVICE_SPINNAX,
              BLE_CHARACTERISTIC_RSP
            )
            .subscribe(this.notificationObserver);

          // Nach dem Start der Notification Info-Paket senden
          await this.sendCmd(new PckCmdInfo()).catch((e: any) => {
            if (
              e instanceof Error &&
              e.message.includes('already a command of type')
            ) {
              logger.warn(`🟡 ble - onConnectionUp - ${e.message} - ignoring`);
              return;
            }
            return this.sendCmd(new PckCmdInfo()); // retry once
          });
          return true;
        } catch (err) {
          logger.error(
            `🔴 ble - onConnectionUp - Error starting notification: ${err}`
          );
          this.updateConnectionState(StatesBLE.DISCONNECTED);
          return false;
        }
      }

      return this.connectionState.get() === StatesBLE.CONNECTED;
    } catch (error) {
      logger.error('Unexpected error in onConnectionUp:', error);
      this.updateConnectionState(StatesBLE.DISCONNECTED);
      return false;
    }
  }

  onConnectionLost(device: BluetoothEntry, manual: boolean = false) {
    if (!manual) {
      logger.info(`Lost connection to device ${device.name} (${device.id})`);
    }
    runInAction(() => this.connectionState.set(StatesBLE.DISCONNECTED));
    this.resetWaitingCommands();
  }

  async disconnect(deviceId?: string): Promise<boolean> {
    try {
      if (!deviceId) {
        const currentDevice = this.bleDevice.get();
        if (!currentDevice?.entry?.id) {
          this.updateConnectionState(StatesBLE.DISCONNECTED);
          return true;
        }
        deviceId = currentDevice.entry.id;
      }

      // Cleanup vor dem Trennen
      this.cleanup(deviceId);

      await this._ble.disconnect(deviceId);
      return true;
    } catch (error) {
      logger.error(
        `🔴 ble - disconnect - Error disconnecting from device ${deviceId}: ${error}`
      );
      // Trotz Fehler aufräumen
      if (deviceId) {
        this.cleanup(deviceId);
      }
      return true;
    }
  }

  async checkConnected(deviceId: string): Promise<boolean> {
    if (Device.platform === 'ios') {
      // ios doesn't show already connected devices on scan so fake it!
      const devices: Array<BluetoothEntry> =
        await this._ble.peripheralsWithIdentifiers([deviceId]);
      if (devices && devices.length) {
        return await this.onConnectionUp(devices[0]);
      }
    }
    return false;
  }

  async list(): Promise<BluetoothEntry[]> {
    return Promise.resolve([]);
  }

  // --------------------------------------------------------------------------
  // Send BLE commands
  // --------------------------------------------------------------------------

  sendCmd = action(async (cmd: BLCmd): Promise<BLRsp | null> => {
    try {
      if (this.connectionState.get() !== StatesBLE.CONNECTED) {
        logger.warn(
          `🟡 ble - sendCmd - not connected, current state: ${this.connectionState.get()}`
        );
        throw new Error(
          `Device not connected, state: ${this.connectionState.get()}`
        );
      }
      const buffer = cmd.toArrayBuffer();
      const theCmdName = cmdName(buffer);

      return new Promise<BLRsp | null>((resolve, reject) => {
        // Basisprüfungen
        if (this.connectionState.get() !== StatesBLE.CONNECTED) {
          reject(
            logger.warn(
              `🔴 ble - sendCmd - not connected, current state: ${this.connectionState.get()}`
            )
          );
          return;
        }

        const device = this.bleDevice.get()?.entry;
        if (!device || device.id === '' || device.id === ID_DEVICE_DEFAULT) {
          reject(new Error('Device ID not valid'));
          return;
        }

        // Prüfe, ob bereits ein Befehl des gleichen Typs läuft
        if (this._waitingCommands[theCmdName]) {
          reject(
            logger.warn(
              `🔴 ble - sendCmd - there is already a command of type ${theCmdName} waiting for a response`
            )
          );
          return;
        }

        const timeout = setTimeout(() => {
          const [_resolve, reject, _timeout] = this._waitingCommands[
            theCmdName
          ] ?? [null, null, null];
          if (!!resolve) {
            _.unset(this._waitingCommands, theCmdName);
            resolve(null);
            logger.warn(
              `🟡 ble - sendCmd - Command ${theCmdName} timed out after 3000ms`
            );
          }
        }, 3000);

        this._waitingCommands[theCmdName] = [resolve, reject, timeout];
        this._ble.writeWithoutResponse(
          device.id,
          BLE_SERVICE_SPINNAX,
          BLE_CHARACTERISTIC_CMD,
          buffer
        );
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      logger.error('🔴 ble - sendCmd - Error:', errorMessage);

      // Spezifische BLE-Fehler abfangen
      if (errorMessage.includes('not connected')) {
        this.updateConnectionState(StatesBLE.DISCONNECTED);
      }

      throw new Error(`BLE command failed: ${errorMessage}`);
    }
  });

  clearCmd(theCmdName: string, forced = true) {
    logger.warn(
      `🟡 ble - clearCmd - clearing command ${theCmdName} (forced: ${forced})`
    );
    const [_resolve, _reject, timeout] = this._waitingCommands[theCmdName] ?? [
      undefined,
      undefined,
      undefined,
    ];

    if (forced)
      _reject?.(
        logger.error(`🔴 ble - clearCmd - Aborted Command ${theCmdName}`)
      );

    // first of all: clear the timeout!
    if (timeout !== undefined) clearTimeout(timeout);

    _.unset(this._waitingCommands, theCmdName);
    logger.debug('waitingCommands', this._waitingCommands);
  }

  onData(payload: ArrayBuffer[]): void {
    if (payload.length === 0) return;

    const buffer = payload[0];
    const theRspName = cmdName(buffer) as keyof BLRsps;
    const theCmdName = theRspName.replace('Rsp', 'Cmd') as keyof BLCmds;

    const ResponseClass = Pck[theRspName];

    if (!ResponseClass?.ofArrayBuffer) {
      logger.error(`🔴 ble - onData - ${theRspName} has no 'ofArrayBuffer'`);
      return;
    }

    const converted = ResponseClass.ofArrayBuffer(buffer);

    if (converted instanceof PckRspLog) {
      // pipe Log messages straight through!
      this.dataObserver.next(converted);
      return;
    }

    const [resolve, reject, timeout] = this._waitingCommands[theCmdName] ?? [
      undefined,
      undefined,
      undefined,
    ];

    if (timeout !== undefined) clearTimeout(timeout);
    _.unset(this._waitingCommands, theCmdName);

    if (!!resolve) {
      if (converted) {
        // logger.debug(`Resolving Answer for '${theCmdName}'`, converted);
        // someone waits for this!
        resolve(converted);
      } else {
        reject(new Error(`Couldn't convert Answer for '${theCmdName}'`));
      }
    } else if (
      !(
        converted instanceof PckRspInfo ||
        converted instanceof PckRspReTrg ||
        converted instanceof PckRspIdBeg
      )
    ) {
      logger.warn(`Got unexpected Answer ${theRspName}`, converted);
    }

    if (!converted) {
      logger.error(`couldn't translate ${theRspName}!`);
      return;
    }

    if (converted instanceof PckRspInfo) {
      const oldDevice = this.bleDevice.get();
      const newDevice = _.assign({}, oldDevice, {
        info: {
          firmwareVersion: numberArrayToString(
            converted.spinnax_freak_version_string
          ),
          firmwareOSVersion: extractOSVersion(converted),
          firmwareId: numberArrayToString(
            converted.spinnax_freak_datetime_string
          ),
        },
      });

      const newState = extractBLEDongleState(converted.dongle_state);
      if (!_.isEqual(newState, this.deviceState))
        runInAction(() => this.deviceState.set(newState));
      if (!_.isEqual(newDevice, oldDevice)) {
        this.setBleDevice(newDevice);
      }
    } else if (converted instanceof PckRspReTrg) {
      for (const sample of converted.samples) this.recordObserver.next(sample);
    }

    // do this nonetheless
    this.dataObserver.next(converted);
  }

  // ✅ Pausiere alle BLE-Aktivitäten für Firmware-Update
  async pauseForUpdate(): Promise<void> {
    logger.info('ble - pauseForUpdate - Pausing BLE service for firmware update');

    try {
      // 1. Stoppe Scanning
      this.stopScan();

      // 2. Stoppe alle Notifications
      const device = this.bleDevice.get()?.entry;
      if (device?.id) {
        try {
          await this._ble.stopNotification(
            device.id,
            BLE_SERVICE_SPINNAX,
            BLE_CHARACTERISTIC_RSP
          );
        } catch (e) {
          logger.warn('ble - pauseForUpdate - Error stopping notifications:', e);
        }
      }

      // 3. Cleanup aller wartenden Commands
      this.resetWaitingCommands();

      // 4. Verbindung sanft trennen
      if (device?.id) {
        await this.disconnect(device.id);
      }

      // 5. BLE-Hardware kurz freigeben
      await new Promise((resolve) => setTimeout(resolve, 2000));

      logger.info('ble - pauseForUpdate - BLE service paused successfully');
    } catch (error) {
      logger.error('ble - pauseForUpdate - Error pausing BLE service:', error);
      throw error;
    }
  }

  async resumeAfterUpdate(): Promise<void> {
    logger.info('ble - resumeAfterUpdate - Resuming BLE service after firmware update');

    try {
      // BLE-Service wieder aktivieren
      // Kurze Pause für Hardware-Stabilisierung
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Status zurücksetzen
      this.updateConnectionState(StatesBLE.DISCONNECTED);

      // Cleanup
      this.resetWaitingCommands();

      logger.info('ble - resumeAfterUpdate - BLE service resumed successfully');
    } catch (error) {
      logger.error('ble - resumeAfterUpdate - Error resuming BLE service:', error);
      throw error;
    }
  }
}

export default BluetoothLEService;
